// client/src/pages/Privacy.js
import { useSettings } from '../hooks/useSettings.js';

export default function Privacy() {
  const { settings } = useSettings();
  const siteName = settings.site_name || 'The Cherry Fairy Shop';

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <h1 className="font-display text-3xl sm:text-4xl text-gold-dark dark:text-gold mb-2">Privacy Policy</h1>
        <p className="text-royal-500 dark:text-royal-200 text-base mb-8">Last updated: July 24, 2026</p>

        <div className="space-y-8 text-royal-700 dark:text-royal-100 text-base leading-relaxed">

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Overview</h2>
            <p>
              {siteName} ("we," "us," or "our") is operated by Todd Davis as a sole proprietorship. 
              This Privacy Policy explains how we collect, use, store, and protect your personal information 
              when you use our website and services at thecherryfairyshop.com.
            </p>
            <p className="mt-3">
              By creating an account or using our services, you agree to the practices described in this policy. 
              If you have questions, contact us at <a href="mailto:thecherryfairies@gmail.com" className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light transition-colors">thecherryfairies@gmail.com</a>.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Information We Collect</h2>
            
            <h3 className="text-royal-900 dark:text-white font-medium mt-4 mb-2">Account Information</h3>
            <p>
              When you register, we collect your email address, first name, and last name. 
              We use this to create and manage your account, send eCards on your behalf, and communicate with you about your account.
            </p>

            <h3 className="text-royal-900 dark:text-white font-medium mt-4 mb-2">Contact Information You Provide</h3>
            <p>
              You may manually enter contacts or import them via CSV upload. For each contact, you may provide 
              a first name, last name, email address, birthday (month and day only — we do not collect birth year), 
              phone number, timezone, and optional notes. This information is used solely to send eCards and manage 
              birthday auto-send features on your behalf.
            </p>

            <h3 className="text-royal-900 dark:text-white font-medium mt-4 mb-2">Google Contacts Data</h3>
            <p>
              If you choose to import contacts from Google, we request read-only access to your Google Contacts 
              using the <code className="text-gold dark:text-gold bg-royal-800 px-1.5 py-0.5 rounded text-xs">contacts.readonly</code> scope 
              through Google's OAuth 2.0 authorization. We retrieve only the following data: first name, last name, 
              email address, and birthday (month and day only). We do not access phone numbers, addresses, photos, 
              or any other contact data from your Google account.
            </p>
            <p className="mt-2">
              Your Google Contacts data is used exclusively to populate your contact list within {siteName} so you can send eCards. We do not store your Google access token beyond the brief moment needed to 
              fetch your contacts. We do not use Google Contacts data for advertising, analytics, or any purpose 
              other than importing contacts at your request.
            </p>
            <p className="mt-2">
              Our use and transfer of information received from Google APIs adheres to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light transition-colors">
                Google API Services User Data Policy
              </a>, including the Limited Use requirements. We do not transfer Google Contacts data to third 
              parties (except as necessary to deliver eCards via our email provider), do not use it for 
              advertising or to build user profiles, and do not permit humans to read it except as needed 
              to provide or improve the contact import feature, comply with applicable law, or investigate 
              security issues.
            </p>

            <h3 className="text-royal-900 dark:text-white font-medium mt-4 mb-2">Microsoft Contacts Data</h3>
            <p>
              If you choose to import contacts from Microsoft, we request read-only access to your Outlook/Microsoft 365 
              contacts through Microsoft's OAuth 2.0 authorization. The same data limitations and usage policies 
              described above for Google Contacts apply equally to Microsoft Contacts data.
            </p>

            <h3 className="text-royal-900 dark:text-white font-medium mt-4 mb-2">Payment Information</h3>
            <p>
              Subscription payments are processed by Stripe. We do not store your credit card number, CVV, 
              or full payment details on our servers. Stripe handles all payment processing securely. 
              We retain only your Stripe customer ID to manage your subscription. 
              For more information, see <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light transition-colors">Stripe's Privacy Policy</a>.
            </p>

            <h3 className="text-royal-900 dark:text-white font-medium mt-4 mb-2">Our Own Usage Data</h3>
            <p>
              We collect basic analytics data when eCards are viewed, including device type, browser, and 
              operating system. This data is used to provide view counts and engagement statistics to eCard senders. 
              It is processed internally within our own system and is not shared with anyone.
            </p>

            <h3 className="text-royal-900 dark:text-white font-medium mt-4 mb-2">Google Analytics</h3>
            <p>
              We also use Google Analytics, a web analytics service provided by Google, to understand how 
              visitors find and use our website. Google Analytics collects information such as the pages you 
              visit and how long you spend on them, the approximate geographic region derived from your IP 
              address, your device type and browser, and the site or search that referred you to us. This 
              information is collected using first-party cookies set in your browser and is shared with Google.
            </p>
            <p className="mt-2">
              Google Analytics runs on all pages of our website, including eCard viewing pages. If you open 
              an eCard someone has sent you, that page view is measured in the same way as any other page on 
              our site.
            </p>
            <p className="mt-2">
              We use Google Analytics only to measure and improve our website. We have not enabled Google's 
              advertising features, remarketing, or Google Signals, and we do not use this data to build 
              advertising profiles. Google Analytics does not receive your name, email address, password, 
              contact list, or the contents of any eCard.
            </p>
            <p className="mt-2">
              You can prevent Google Analytics from collecting your activity by installing the{' '}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light transition-colors">
                Google Analytics Opt-out Browser Add-on
              </a>, or by using your browser's tracking protection or a content blocker. Opting out does not 
              affect your ability to use any part of our site. For more information on how Google handles this 
              data, see{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light transition-colors">
                Google's Privacy Policy
              </a>.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <p className="mt-2">
              Create and manage your account; send, schedule, and deliver eCards to your designated recipients; 
              import contacts from Google or Microsoft at your request; process subscription payments through Stripe; 
              provide analytics and engagement data for your sent eCards; understand how visitors use our website 
              so we can improve it; send you account-related communications 
              (such as password resets and subscription confirmations); and improve and maintain our services.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Data Sharing and Disclosure</h2>
            <p>
              We do not sell, rent, trade, or share your personal information with third parties for marketing 
              or advertising purposes. We will never monetize your data.
            </p>
            <p className="mt-3">We share data only with the following service providers, solely to operate our platform:</p>
            <p className="mt-2">
              <span className="text-royal-900 dark:text-white font-medium">Stripe</span> — processes subscription payments. 
              Stripe receives your payment information directly; we do not handle or store it.
            </p>
            <p className="mt-2">
              <span className="text-royal-900 dark:text-white font-medium">Resend</span> — delivers eCard notification emails 
              on your behalf. Resend receives recipient email addresses and eCard delivery content only as 
              needed to send the email.
            </p>
            <p className="mt-2">
              <span className="text-royal-900 dark:text-white font-medium">Google Analytics</span> — receives website 
              usage information as described above. Google does not receive your account details, contacts, 
              or eCard content.
            </p>
            <p className="mt-3">
              We may disclose information if required by law, such as in response to a court order or legal process.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Data Storage and Security</h2>
            <p>
              Your data is stored in a secured database hosted by our infrastructure provider. 
              Access to the database is restricted to the site administrator only. 
              All connections to our website use HTTPS encryption. Passwords are hashed and never stored in plain text. 
              We do not store Google or Microsoft OAuth tokens beyond the momentary exchange needed to import contacts.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Data Retention and Deletion</h2>
            <p>
              Your account data and contacts are retained as long as your account is active. 
              You may delete individual contacts at any time. If you request account deletion, 
              your account is scheduled for permanent removal within 30 days, after which all associated 
              data — including contacts, eCards, and analytics — is permanently deleted.
            </p>
            <p className="mt-3">
              Website usage data held by Google Analytics is retained according to the retention period 
              configured in that service and is deleted by Google on that schedule. Because this data is 
              not linked to your account, deleting your account does not remove it.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Your Rights</h2>
            <p>You have the right to:</p>
            <p className="mt-2">
              Access the personal data we hold about you; correct inaccurate information via your Account page; 
              delete your contacts or your entire account at any time; revoke Google or Microsoft access to your 
              contacts at any time through your Google Account or Microsoft Account settings respectively; 
              opt out of Google Analytics as described above; and 
              contact us with any privacy-related questions or concerns.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Cookies</h2>
            <p>
              We use a JSON Web Token (JWT) stored in your browser's local storage to keep you signed in. 
              This is not a cookie and is not shared with any third party.
            </p>
            <p className="mt-3">
              We also use first-party cookies set by Google Analytics (named{' '}
              <code className="text-gold dark:text-gold bg-royal-800 px-1.5 py-0.5 rounded text-xs">_ga</code> and 
              similar) to tell one visitor from another and measure how our site is used. These cookies do not 
              identify you to us by name or email. You can block or delete them through your browser settings, 
              or opt out using the link in the Google Analytics section above.
            </p>
            <p className="mt-3">
              We do not use advertising or retargeting cookies, and we do not allow third parties to place 
              advertising cookies on our site.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Children's Privacy</h2>
            <p>
              Our services are not directed to children under 13, and children under 13 may not create accounts. 
              However, our household members feature allows adult account holders to store a child's first name 
              and birthday (month and day only — no birth year) under a parent or guardian's contact record for 
              the purpose of sending birthday eCards. This minimal information is provided and managed entirely 
              by the parent or guardian, and eCards are delivered to the parent or guardian's email address, never 
              directly to a child.
            </p>
            <p className="mt-3">
              We do not knowingly collect personal information directly from children under 13. If you believe 
              a child has independently provided us with personal information without parental consent, please 
              contact us and we will delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. If we make significant changes, 
              we will notify registered users via email. The "Last updated" date at the top of this page 
              indicates when the policy was last revised.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, contact us at:
            </p>
            <p className="mt-2">
              <span className="text-royal-900 dark:text-white font-medium">{siteName}</span><br />
              Todd Davis<br />
              <a href="mailto:thecherryfairies@gmail.com" className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light transition-colors">
                thecherryfairies@gmail.com
              </a>
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}